import { useState } from 'react';
import { LangfuseWeb } from "langfuse";
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { cn } from "@/lib/utils";

interface MessageFeedbackProps {
  traceId: string;
  messageId?: string;
  className?: string;
}

export function MessageFeedback({ traceId, messageId, className }: MessageFeedbackProps) {
  const [feedback, setFeedback] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Initialize LangfuseWeb client
  const langfuseWeb = new LangfuseWeb({
    publicKey: process.env.NEXT_PUBLIC_LANGFUSE_PUBLIC_KEY,
    baseUrl: process.env.NEXT_PUBLIC_LANGFUSE_BASE_URL || "https://cloud.langfuse.com"
  });

  const handleFeedback = async (value: number) => {
    if (isLoading) return;
    
    setIsLoading(true);
    
    try {
      await langfuseWeb.score({
        traceId: traceId,
        name: "user-feedback",
        value,
        comment: messageId ? `Message ID: ${messageId}` : undefined
      });
      
      setFeedback(value);
      console.log(`[MessageFeedback] Feedback submitted: ${value} for trace ${traceId}`);
    } catch (error) {
      console.error('[MessageFeedback] Error submitting feedback:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <button
        onClick={() => handleFeedback(1)}
        disabled={isLoading}
        className={cn(
          "inline-flex items-center justify-center rounded-md transition-all duration-200",
          "h-6 px-2 text-xs",
          "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "disabled:pointer-events-none disabled:opacity-50",
          feedback === 1 
            ? "text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20" 
            : "text-muted-foreground hover:bg-accent hover:text-foreground",
          isLoading && "opacity-50 cursor-not-allowed"
        )}
        aria-label="Good response"
      >
        <ThumbsUp className="h-3.5 w-3.5" />
      </button>
      
      <button
        onClick={() => handleFeedback(0)}
        disabled={isLoading}
        className={cn(
          "inline-flex items-center justify-center rounded-md transition-all duration-200",
          "h-6 px-2 text-xs",
          "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "disabled:pointer-events-none disabled:opacity-50",
          feedback === 0 
            ? "text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20" 
            : "text-muted-foreground hover:bg-accent hover:text-foreground",
          isLoading && "opacity-50 cursor-not-allowed"
        )}
        aria-label="Poor response"
      >
        <ThumbsDown className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}